import { useEffect } from 'react';

/**
 * Phone layout for every data table in the console.
 *
 * Below 768px a sideways-scrolling table hides its Actions column. This hook
 * watches the page and, for each <table> inside `rootRef`:
 *   - adds the `rt-cards` class (index.css turns rows into cards on phones)
 *   - copies each column header onto its cells as `data-label`, so every
 *     card line reads "Status   Active", "Users   9", …
 *   - marks full-width cells (colSpan > 1, e.g. "No users found") as `rt-full`
 *
 * Opt a table out with `data-no-cards` (e.g. dense editors).
 */
const label = (table) => {
  if (table.hasAttribute('data-no-cards')) return;
  const headers = Array.from(table.querySelectorAll('thead th')).map((th) =>
    (th.getAttribute('aria-label') || th.textContent || '').trim()
  );
  if (headers.length === 0) return;
  table.classList.add('rt-cards');

  table.querySelectorAll('tbody tr').forEach((tr) => {
    let col = 0;
    Array.from(tr.children).forEach((cell) => {
      const span = Number(cell.getAttribute('colspan')) || 1;
      if (span > 1) {
        cell.classList.add('rt-full');
      } else {
        const name = headers[col] || '';
        if (cell.getAttribute('data-label') !== name) cell.setAttribute('data-label', name);
        if (/^actions?$/i.test(name)) cell.classList.add('rt-actions');
      }
      col += span;
    });
  });
};

export default function useResponsiveTables(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof MutationObserver === 'undefined') return undefined;

    let frame = null;
    const run = () => {
      frame = null;
      root.querySelectorAll('table').forEach(label);
    };
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(run);
    };

    run();
    const observer = new MutationObserver(schedule);
    observer.observe(root, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [rootRef]);
}
